import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { RouterlessModal } from '@/se/components/Modal';
import type { DelayReasonModalProps } from '../../types/PatientScreenTypes';

/**
 * Delay reason picker modal
 * Allows selecting a reason for delays in patient flow
 */
export function PatientDelayReasonModal({ delayType, onSubmit, onCancel }: DelayReasonModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [customReason, setCustomReason] = useState<string>('');

  const handleSubmit = useCallback(async () => {
    const reason = selectedReason === 'other' ? customReason : selectedReason;
    if (reason) {
      await onSubmit(reason);
      onCancel(); // Close modal after submission
    }
  }, [selectedReason, customReason, onSubmit, onCancel]);

  // Get reasons based on delay type
  const reasons = getDelayReasons(delayType);

  return (
    <RouterlessModal title={`${getDelayTitle(delayType)} - Delay Reason`} onClose={onCancel}>
      <Content>
        <ReasonsList>
          {reasons.map(reason => (
            <ReasonButton
              key={reason.value}
              selected={selectedReason === reason.value}
              onClick={() => setSelectedReason(reason.value)}
            >
              {reason.label}
            </ReasonButton>
          ))}
        </ReasonsList>

        {selectedReason === 'other' && (
          <CustomReasonInput
            placeholder="Enter custom reason..."
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
            autoFocus
          />
        )}

        <SubmitButton
          disabled={!selectedReason || (selectedReason === 'other' && !customReason)}
          onClick={handleSubmit}
        >
          Submit
        </SubmitButton>
      </Content>
    </RouterlessModal>
  );
}

function getDelayTitle(delayType: string): string {
  switch (delayType) {
    case 'pacuOut':
      return 'PACU Discharge';
    case 'oob':
      return 'Out of Bed';
    case 'pt':
      return 'Physical Therapy';
    case 'ptComplete':
      return 'PT Complete';
    default:
      return 'Action';
  }
}

function getDelayReasons(delayType: string) {
  const commonReasons = [
    { value: 'patient_not_ready', label: 'Patient Not Ready' },
    { value: 'waiting_for_physician', label: 'Waiting for Physician' },
    { value: 'waiting_for_transport', label: 'Waiting for Transport' },
    { value: 'waiting_for_family', label: 'Waiting for Family' },
    { value: 'medical_issue', label: 'Medical Issue' },
    { value: 'other', label: 'Other (Specify)' },
  ];
  const mapLabelsToReasons = (labels: string[]) =>
    labels.map(label => ({
      value: label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, ''),
      label,
    }));

  const ptReasons = mapLabelsToReasons(['Spinal', 'Sedation', 'Quad Weakness', 'Pain', 'N/V', 'Orthostatic', 'Other']);

  const oobOptions = mapLabelsToReasons(['Nausea or Vomiting',
    'Sedation',
    'Spinal',
    'Pain',
    'Refusal',
    'Orthostatic',
    'Other',
  ]);

  const ptCompleteReasons = mapLabelsToReasons([
    'Comorbidities',
    'Staffing',
    'Carepartner',
    'Spinal',
    'Sedation',
    'Pain',
    'Orthostatic',
    'Quad Weakness',
    'POUR',
    'Lengthy discharge',
    'Other',
  ]);

  // Can customize reasons per delay type if needed
  switch (delayType) {
    case 'pacuOut':
      return [
        { value: 'pain_management', label: 'Pain Management' },
        { value: 'nausea', label: 'Nausea/Vomiting' },
        { value: 'vital_signs', label: 'Vital Signs Not Stable' },
        ...commonReasons,
      ];
    case 'oob':
      return oobOptions;
    case 'pt':
      return ptReasons;
    case 'ptComplete':
      return ptCompleteReasons;
    default:
      return commonReasons;
  }
}

const Content = styled.div`
  padding: 0 1em 1em;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 400px;
  overflow-y: auto;
`;

const ReasonButton = styled.button<{ selected: boolean }>`
  padding: 1rem;
  border: 2px solid ${props => (props.selected ? '#00b641' : 'rgba(255, 255, 255, 0.2)')};
  background: ${props => (props.selected ? 'rgba(0, 182, 65, 0.2)' : 'rgba(255, 255, 255, 0.05)')};
  color: #fff;
  font-size: 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s;

  &:hover {
    background: ${props => (props.selected ? 'rgba(0, 182, 65, 0.3)' : 'rgba(255, 255, 255, 0.1)')};
  }
`;

const CustomReasonInput = styled.input`
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  border-radius: 0.25rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #00b641;
  }
`;

const SubmitButton = styled.button`
  padding: 1rem 2rem;
  border: none;
  background: #00b641;
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #00a038;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
